import { Link } from "react-router-dom";
import { TableCell, TableRow } from "../../../../components/ui/common/table";
import { MappingSourceType } from "../../../../config/enums/MappingSourceType.enum";
import { ProductRowViewModel } from "../types/productListView.types";
import ProductRowActions from "./ProductRowActions";
import { productTableColumnClass } from "./productTable.columns";

interface ProductTableRowProps {
  row: ProductRowViewModel;
  onView: (row: ProductRowViewModel) => void;
  onEdit: (row: ProductRowViewModel) => void;
  onDelete: (row: ProductRowViewModel) => void;
}

/**
 * One product's row. Mirrors the legacy `renderSalesUnits` + inline cell
 * markup exactly (see src/screens/product/productList/ProductList.tsx): an
 * average product shows its sales unit names as plain text and one
 * aggregated climate impact value, while a regular product links each sales
 * unit name to the mapping tool and shows a climate impact value per unit.
 */
const ProductTableRow = ({ row, onView, onEdit, onDelete }: ProductTableRowProps) => {
  return (
    <TableRow className="tw-bg-white hover:tw-bg-muted/50">
      <TableCell className={productTableColumnClass.name}>
        <div className="tw-font-medium tw-text-foreground">{row.product_name}</div>
        <div className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-xs tw-text-muted-foreground">
          <span className="tw-font-mono">{row.registerNumber}</span>
          <span>&middot;</span>
          <span className="tw-font-mono tw-uppercase">{row.productType}</span>
          <span>&middot;</span>
          <span className={row.isAverageProduct ? "tw-font-medium tw-text-primary" : undefined}>
            Avg Impact: {row.averageImpactStatusText}
          </span>
        </div>
      </TableCell>
      <TableCell className={productTableColumnClass.salesUnit}>
        {row.salesUnits.length === 0 ? (
          <span className="tw-text-foreground">-</span>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {row.salesUnits.map(unit =>
              row.isAverageProduct ? (
                <span key={unit.id} className="tw-text-foreground">
                  {unit.name}
                </span>
              ) : (
                <Link
                  key={unit.id}
                  to={`/mapping/${MappingSourceType.product}?sourceId=${unit.id}`}
                  className="tw-text-primary tw-underline-offset-2 hover:tw-underline"
                >
                  {unit.name}
                </Link>
              )
            )}
          </div>
        )}
      </TableCell>
      <TableCell className={productTableColumnClass.climateImpact}>
        {row.isAverageProduct ? (
          <span className="tw-text-foreground">{row.averageClimateImpactText}</span>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {row.salesUnits.map(unit => (
              <span key={unit.id} className="tw-text-foreground">
                {unit.climateImpactText}
              </span>
            ))}
          </div>
        )}
      </TableCell>
      <TableCell className={productTableColumnClass.eanBarcode}>
        <div className="tw-flex tw-flex-col tw-gap-1">
          {row.salesUnits.map(unit => (
            <span key={unit.id} className="tw-text-foreground">
              {unit.eanBarcode}
            </span>
          ))}
        </div>
      </TableCell>
      <TableCell className={productTableColumnClass.actions}>
        <ProductRowActions row={row} onView={onView} onEdit={onEdit} onDelete={onDelete} />
      </TableCell>
    </TableRow>
  );
};

export default ProductTableRow;
